import React, { useEffect, useState, useMemo } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { useAuth } from "../../AuthContext";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Checkbox } from "primereact/checkbox";

import "primeicons/primeicons.css";
import "./style.css";

import {
  FaClipboardList,
  FaClock,
  FaInfoCircle,
  FaCalendarAlt,
  FaIdCard,
  FaCity,
  FaBuilding,
  FaCheckCircle,
  FaList,
} from "react-icons/fa";

const Pedidos = () => {
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [visibleNovoPedido, setVisibleNovoPedido] = useState(false);
  const [visibleEditarPedido, setVisibleEditarPedido] = useState(false);
  const [visibleItens, setVisibleItens] = useState(false);

  //Cadastro
  const [pedidoEditandoId, setPedidoEditandoId] = useState(null);
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [empresaId, setEmpresaId] = useState("");
  const [ativo, setAtivo] = useState(true);

  // Itens do Pedido
  const [pedidoItensEditandoId, setPedidoItensEditandoId] = useState(null);
  const [itensEditando, setItensEditando] = useState([]);

  // Estados para os dialogs de confirmação
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  // Estados para Deletar
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [itemDelete, setItemDelete] = useState(null);

  //Filtro
  const [mostrarFiltroPedidos, setMostrarFiltroPedidos] = useState(false);
  const [filtroDescricao, setFiltroDescricao] = useState("");
  const [filtroTitulo, setFiltroTitulo] = useState("");

  // Opções
  const [empresasOptions, setEmpresasOptions] = useState([]);

  useEffect(() => {
    fetchPedidos();
    fetchEmpresas();
  }, []);

  // Pedidos filtrados e ordenados
  const pedidosFiltrados = useMemo(() => {
    return pedidos.filter((pedido) => {
      // Filtro por título (se filtroTitulo não estiver vazio)
      const tituloMatch =
        !filtroTitulo ||
        pedido.titulo?.toLowerCase().includes(filtroTitulo.toLowerCase());

      // Filtro por descrição (se filtroDescricao não estiver vazio)
      const descricaoMatch =
        !filtroDescricao ||
        pedido.descricao?.toLowerCase().includes(filtroDescricao.toLowerCase());

      // Retorna true apenas se ambos os filtros corresponderem
      return tituloMatch && descricaoMatch;
    });
  }, [pedidos, filtroTitulo, filtroDescricao]);

  const fetchPedidos = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      const response = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/pedidos",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Erro ao buscar pedidos");
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.data)) {
        console.log("Pedidos = ", data.data);
        setPedidos(data.data);
        setError(null);
      } else {
        setPedidos([]);
        setError("Nenhum pedido encontrado.");
      }
    } catch (error) {
      setError(error.message);
      setPedidos([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpresas = async () => {
    try {
      if (!user?.token) return;

      const response = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/empresas",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          const options = data.data.map((empresa) => ({
            label: empresa.nome,
            value: empresa.id,
          }));
          setEmpresasOptions(options);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar empresas:", error);
    }
  };

  const resetarCampos = () => {
    setTitulo("");
    setDescricao("");
    setEmpresaId("");
    setAtivo(true);
  };

  const handleSalvar = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      // Verificação dos campos obrigatórios
      if (!titulo || !empresaId) {
        setDialogMessage("Preencha todos os campos obrigatórios");
        setShowErrorDialog(true);
        return;
      }

      const pedidoData = {
        titulo: titulo,
        descricao: descricao,
        empresa_id: empresaId,
        ativo: ativo ? "1" : "0",
      };

      const response = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/pedidos",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
          body: JSON.stringify(pedidoData),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Falha ao cadastrar pedido");
      }

      await response.json();
      setDialogMessage("Pedido cadastrado com sucesso!");
      setShowSuccessDialog(true);
      fetchPedidos();
      setVisibleNovoPedido(false);
      resetarCampos();
    } catch (err) {
      console.error("Erro detalhado ao salvar:", err);
      setDialogMessage(err.message || "Erro ao cadastrar pedido");
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = async (pedidoId) => {
    setLoading(true);
    setError(null);

    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      if (!titulo || !empresaId) {
        setDialogMessage("Preencha todos os campos obrigatórios");
        setShowErrorDialog(true);
        return;
      }

      const pedidoData = {
        titulo: titulo,
        descricao: descricao,
        empresa_id: empresaId,
        ativo: ativo ? "1" : "0",
      };

      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/pedidos/${pedidoId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
          body: JSON.stringify(pedidoData),
        },
      );

      const responseData = await response.json();

      if (!response.ok) {
        const errorMessage =
          responseData?.message ||
          responseData?.data?.message ||
          `Erro HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      setDialogMessage("Pedido atualizado com sucesso!");
      setShowSuccessDialog(true);
      fetchPedidos();
      setVisibleEditarPedido(false);
      resetarCampos();
    } catch (err) {
      console.error("Erro na edição:", err);
      setDialogMessage(
        err.message.includes("não encontrado")
          ? "Pedido não encontrado ou já removido"
          : err.message,
      );
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  // Função para abrir o diálogo de confirmação
  const openDeleteConfirm = (id) => {
    setItemDelete(id);
    setShowConfirmDialog(true);
  };

  const handleExcluir = async (itemDelete) => {
    setLoading(true);
    setError(null);

    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      console.log("itemDelete = ", itemDelete);

      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/pedidos/${itemDelete}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
        },
      );

      const responseData = await response.json();

      if (!response.ok) {
        const errorMessage =
          responseData?.message ||
          responseData?.data?.message ||
          `Erro HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      setDialogMessage("Pedido excluído com sucesso!");
      setShowSuccessDialog(true);
      fetchPedidos();
      setShowConfirmDialog(false);
    } catch (err) {
      console.error("Erro na exclusão:", err);
      setDialogMessage(
        err.message.includes("não encontrado")
          ? "Pedido não encontrado ou já removido"
          : err.message,
      );
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  //----------------------------------------------------------------------
  //--------------------------- ITENS DO PEDIDO --------------------------
  //----------------------------------------------------------------------
  const handleAdicionarItem = () => {
    setItensEditando((prev) => [
      ...prev,
      { id: null, descricao: "", quantidade: 1 },
    ]);
  };

  // Função para marcar/desmarcar item como verificado
  const handleVerificarItem = (index) => {
    const novosItens = [...itensEditando];
    novosItens[index] = {
      ...novosItens[index],
      verificado: !novosItens[index].verificado, // Alterna entre true/false
    };
    setItensEditando(novosItens);
  };

  // Função para remover item (só se não estiver verificado)
  const handleRemoverItem = (index) => {
    if (!itensEditando[index].verificado) {
      const novosItens = [...itensEditando];
      novosItens.splice(index, 1);
      setItensEditando(novosItens);
    }
  };

  // Função para atualizar item
  const handleAtualizarItem = (index, campo, valor) => {
    const novosItens = [...itensEditando];
    if (!novosItens[index].verificado) {
      novosItens[index] = {
        ...novosItens[index],
        [campo]: valor,
      };
      setItensEditando(novosItens);
    }
  };

  const handleSalvarItens = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado.");
      }

      if (!pedidoItensEditandoId) {
        throw new Error("ID do pedido não definido.");
      }

      // Filtra itens válidos (permite array vazio)
      const itensValidos = (itensEditando || [])
        .map((item) => ({
          id: item.id ?? null,
          descricao: (item.descricao || "").trim(),
          quantidade: parseInt(item.quantidade) || 1,
          verificado: item.verificado ? 1 : 0, // CORRIGIDO: 1 para true, 0 para false
        }))
        .filter((item) => item.descricao !== "");

      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/pedidos/${pedidoItensEditandoId}/itens`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
          body: JSON.stringify({
            itens: itensValidos,
          }),
        },
      );

      const result = await response.json().catch(() => null);

      console.log("Resposta do servidor:", result);

      if (response.ok && result?.success) {
        // Buscar novamente para ter os IDs atualizados
        await buscarItensPedido(pedidoItensEditandoId);
        setDialogMessage(
          itensValidos.length === 0
            ? "Todos os itens foram removidos!"
            : "Itens salvos com sucesso!",
        );
        setShowSuccessDialog(true);
        setVisibleItens(false);
      } else {
        const msg =
          result?.message ||
          result?.data?.message ||
          `Erro ${response.status}: ${response.statusText}`;
        throw new Error(msg);
      }
    } catch (error) {
      console.error("Erro ao salvar itens:", error);
      setDialogMessage("Erro ao salvar itens: " + (error.message || error));
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  // Buscar itens existentes
  const buscarItensPedido = async (pedidoId) => {
    try {
      if (!user?.token) return;

      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/pedidos/${pedidoId}/itens`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) throw new Error("Erro ao buscar itens");

      const data = await response.json();

      if (data.success) {
        // Converter verificado de STRING "0"/"1" para boolean true/false
        const itensFormatados = data.data.map((item) => {
          console.log("Item:", item, "Verificado (string):", item.verificado);

          // Converter string "0" ou "1" para boolean
          // "0" → false, "1" → true, qualquer outra coisa → false
          const verificadoBoolean = item.verificado === "1";

          // console.log('Verificado (boolean):', verificadoBoolean);

          return {
            id: item.id,
            pedido_id: item.pedido_id,
            descricao: item.descricao || "",
            quantidade: parseInt(item.quantidade) || 1,
            verificado: verificadoBoolean,
            data_verificacao: item.data_verificacao,
            data_verificacao_formatada: item.data_verificacao_formatada,
            data_cadastro_formatada: item.data_cadastro_formatada,
          };
        });

        console.log("Itens formatados final:", itensFormatados);
        setItensEditando(itensFormatados);
        return itensFormatados;
      }
    } catch (error) {
      console.error("Erro ao buscar itens:", error);
    }
  };

  // ----------------- Fim Itens do Pedido -----------------

  {
    /* Return Principal */
  }
  return (
    <div
      style={{
        fontFamily: "'Rajdhani', sans-serif",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      {/* Dialog de Sucesso */}
      <Dialog
        visible={showSuccessDialog}
        style={{ width: "350px", fontFamily: "'Rajdhani', sans-serif" }}
        onHide={() => setShowSuccessDialog(false)}
        draggable={false}
        closable={false}
        header={null}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            padding: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "25px",
              height: "25px",
              borderRadius: "50%",
              color: "#22C55E",
              fontSize: "2.2rem",
            }}
          >
            <i
              className="pi pi-check-circle"
              style={{ fontSize: "2.5rem" }}
            ></i>
          </div>
          <p
            style={{
              textAlign: "center",
              fontSize: "1rem",
              margin: 0,
              color: "#374151",
            }}
          >
            {dialogMessage}
          </p>
          <Button
            label="OK"
            icon="pi pi-check"
            onClick={() => setShowSuccessDialog(false)}
            className="p-button-success"
            style={{
              padding: "6px 20px",
              fontWeight: "bold",
              fontSize: "0.9rem",
            }}
          />
        </div>
      </Dialog>

      {/* Dialog de Erro */}
      <Dialog
        visible={showErrorDialog}
        style={{ width: "350px", fontFamily: "'Rajdhani', sans-serif" }}
        onHide={() => setShowErrorDialog(false)}
        draggable={false}
        closable={false}
        header={null}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            padding: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "25px",
              height: "25px",
              borderRadius: "50%",
              color: "#EF4444",
              fontSize: "2.2rem",
            }}
          >
            <i
              className="pi pi-times-circle"
              style={{ fontSize: "2.5rem" }}
            ></i>
          </div>
          <p
            style={{
              textAlign: "center",
              fontSize: "1rem",
              margin: 0,
              color: "#374151",
            }}
          >
            {dialogMessage}
          </p>
          <Button
            label="OK"
            onClick={() => setShowErrorDialog(false)}
            className="p-button-danger"
            style={{
              padding: "6px 20px",
              fontWeight: "bold",
              fontSize: "0.9rem",
            }}
          />
        </div>
      </Dialog>

      {/* Dialog de Confirmação */}
      <Dialog
        visible={showConfirmDialog}
        style={{ width: "350px", fontFamily: "'Rajdhani', sans-serif" }}
        onHide={() => setShowConfirmDialog(false)}
        draggable={false}
        closable={false}
        header={null}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            padding: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "25px",
              height: "25px",
              borderRadius: "50%",
              color: "#ef4444",
              fontSize: "2.2rem",
            }}
          >
            <i
              className="pi pi-question-circle"
              style={{ fontSize: "2.5rem" }}
            ></i>
          </div>
          <p
            style={{
              textAlign: "center",
              fontSize: "1rem",
              margin: 0,
              color: "#374151",
            }}
          >
            Tem certeza que deseja excluir este pedido?
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              width: "100%",
            }}
          >
            <Button
              label="NÃO"
              icon="pi pi-times"
              onClick={() => setShowConfirmDialog(false)}
              className="p-button-outlined p-button-sm"
              style={{
                outline: "none",
                boxShadow: "none",
                padding: "6px 20px",
                minWidth: "100px",
                borderColor: "#dee2e6",
                color: "#495057",
                fontWeight: "500",
                fontSize: "0.875rem",
              }}
            />
            <Button
              label="SIM"
              icon="pi pi-check"
              onClick={() => {
                handleExcluir(itemDelete);
                setShowConfirmDialog(false);
              }}
              className="p-button-success p-button-sm"
              style={{
                outline: "none",
                boxShadow: "none",
                padding: "6px 20px",
                minWidth: "100px",
                fontWeight: "500",
                fontSize: "0.875rem",
              }}
            />
          </div>
        </div>
      </Dialog>

      {/* Dialog Novo Pedido */}
      <Dialog
        visible={visibleNovoPedido}
        style={{
          width: "90vw",
          maxWidth: "768px",
          fontFamily: "'Rajdhani', sans-serif",
        }}
        onHide={() => {
          if (!loading) {
            resetarCampos();
            setVisibleNovoPedido(false);
          }
        }}
        draggable={false}
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "-45px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
              }}
            >
              <div style={{ marginRight: "8px", paddingLeft: "8px" }}>
                <FaList style={{ fontSize: "2.4rem", color: "#B83732" }} />
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", flex: 1 }}
              >
                <div
                  style={{
                    fontSize: "0.9rem",
                    width: "100%",
                    letterSpacing: "1px",
                  }}
                >
                  ADICIONAR
                </div>
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    marginTop: "-10px",
                  }}
                >
                  PEDIDO
                </div>
              </div>
            </div>
          </div>
        }
      >
        <>
          {/* Formulário em Cards */}
          <div style={{ padding: "20px 0" }}>
            {/* 1ª Linha - Título e Descrição */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "10px",
                marginBottom: "10px",
              }}
            >
              {/* Card Título */}
              <div
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #e8e8e8",
                  padding: "15px",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      paddingBottom: "5px",
                      borderBottom: "2px solid #3498DB",
                    }}
                  >
                    <FaList style={{ color: "#3498DB", fontSize: "0.9rem" }} />
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#2c3e50",
                        fontSize: "0.95rem",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Título do Pedido *
                    </span>
                  </div>
                  <InputText
                    className="p-inputtext-sm"
                    placeholder="Título do pedido"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    style={{
                      width: "100%",
                      height: "35px",
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                  />
                </div>
              </div>
              {/* Card Descrição */}
              <div
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #e8e8e8",
                  padding: "15px",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      paddingBottom: "5px",
                      borderBottom: "2px solid #E67E22",
                    }}
                  >
                    <FaList style={{ color: "#E67E22", fontSize: "0.9rem" }} />
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#2c3e50",
                        fontSize: "0.95rem",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Descrição do Pedido *
                    </span>
                  </div>
                  <InputText
                    className="p-inputtext-sm"
                    placeholder="Descrição do pedido"
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    style={{
                      width: "100%",
                      height: "35px",
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* 2ª Linha - Empresa e Ativo */}
            <div className="grid-empresa-ativo">
              {/* Card Endereço */}
              <div
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #e8e8e8",
                  padding: "15px",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      paddingBottom: "5px",
                      borderBottom: "2px solid #E74C3C",
                    }}
                  >
                    <FaBuilding
                      style={{ color: "#E74C3C", fontSize: "0.9rem" }}
                    />
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#2c3e50",
                        fontSize: "0.95rem",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Empresa *
                    </span>
                  </div>
                  <Dropdown
                    value={empresaId}
                    options={empresasOptions}
                    onChange={(e) => setEmpresaId(e.value)}
                    placeholder="Selecione a empresa"
                    style={{
                      width: "100%",
                      height: "35px",
                      fontFamily: "'Rajdhani', sans-serif",
                      alignItems: "center",
                    }}
                    showClear
                    itemTemplate={(option) => (
                      <div
                        style={{
                          fontFamily: "'Rajdhani', sans-serif",
                          fontSize: "0.85rem",
                        }}
                      >
                        {option.label}
                      </div>
                    )}
                    valueTemplate={(option, props) => {
                      if (!option)
                        return (
                          <span
                            style={{
                              fontFamily: "'Rajdhani', sans-serif",
                              fontSize: "0.85rem",
                            }}
                          >
                            {props.placeholder}
                          </span>
                        );
                      return (
                        <span style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                          {option.label}
                        </span>
                      );
                    }}
                  />
                </div>
              </div>

              {/* Card Ativo */}
              <div
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #e8e8e8",
                  padding: "15px",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      paddingBottom: "5px",
                      borderBottom: "2px solid #2980B9",
                    }}
                  >
                    <FaCheckCircle
                      style={{ color: "#2980B9", fontSize: "0.9rem" }}
                    />
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#2c3e50",
                        fontSize: "0.95rem",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Ativo *
                    </span>
                  </div>
                  <Checkbox
                    checked={ativo}
                    onChange={(e) => setAtivo(e.checked)}
                    style={{
                      width: "100%",
                      height: "35px",
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Botão Fechar */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "15px",
              flexWrap: "wrap",
            }}
          >
            <Button
              label="CANCELAR"
              icon="pi pi-times"
              onClick={() => {
                resetarCampos();
                setVisibleNovoPedido(false);
              }}
              className="p-button-outlined p-button-sm"
              style={{
                outline: "none",
                boxShadow: "none",
                padding: "8px 24px",
                minWidth: "120px",
                borderColor: "#dee2e6",
                color: "#495057",
                fontWeight: "500",
                fontSize: "0.875rem",
              }}
              disabled={loading}
            />
            <Button
              label={loading ? "SALVANDO..." : "CADASTRAR PEDIDO"}
              icon="pi pi-check"
              onClick={handleSalvar}
              className="p-button-success p-button-sm"
              style={{
                outline: "none",
                boxShadow: "none",
                padding: "8px 24px",
                minWidth: "160px",
                fontWeight: "500",
                fontSize: "0.875rem",
              }}
              disabled={loading || !titulo || !descricao || !empresaId}
              loading={loading}
            />
          </div>
        </>
      </Dialog>

      {/* Dialog Editar Pedido */}
      <Dialog
        visible={visibleEditarPedido}
        style={{
          width: "90vw",
          maxWidth: "768px",
          fontFamily: "'Rajdhani', sans-serif",
        }}
        onHide={() => {
          if (!loading) {
            resetarCampos();
            setVisibleEditarPedido(false);
          }
        }}
        draggable={false}
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "-45px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
              }}
            >
              <div style={{ marginRight: "8px", paddingLeft: "8px" }}>
                <FaList style={{ fontSize: "2.4rem", color: "#B83732" }} />
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", flex: 1 }}
              >
                <div
                  style={{
                    fontSize: "0.9rem",
                    width: "100%",
                    letterSpacing: "1px",
                  }}
                >
                  EDITAR
                </div>
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    marginTop: "-10px",
                  }}
                >
                  PEDIDO
                </div>
              </div>
            </div>
          </div>
        }
      >
        <>
          {/* Formulário em Cards */}
          <div style={{ padding: "20px 0" }}>
            {/* 1ª Linha - Título e Descrição */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "10px",
                marginBottom: "10px",
              }}
            >
              {/* Card Título */}
              <div
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #e8e8e8",
                  padding: "15px",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      paddingBottom: "5px",
                      borderBottom: "2px solid #3498DB",
                    }}
                  >
                    <FaList style={{ color: "#3498DB", fontSize: "0.9rem" }} />
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#2c3e50",
                        fontSize: "0.95rem",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Título do Pedido *
                    </span>
                  </div>
                  <InputText
                    className="p-inputtext-sm"
                    placeholder="Título do pedido"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    style={{
                      width: "100%",
                      height: "35px",
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                  />
                </div>
              </div>
              {/* Card Descrição */}
              <div
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #e8e8e8",
                  padding: "15px",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      paddingBottom: "5px",
                      borderBottom: "2px solid #E67E22",
                    }}
                  >
                    <FaList style={{ color: "#E67E22", fontSize: "0.9rem" }} />
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#2c3e50",
                        fontSize: "0.95rem",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Descrição do Pedido *
                    </span>
                  </div>
                  <InputText
                    className="p-inputtext-sm"
                    placeholder="Descrição do pedido"
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    style={{
                      width: "100%",
                      height: "35px",
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* 2ª Linha - Empresa e Ativo */}
            <div className="grid-empresa-ativo">
              {/* Card Endereço */}
              <div
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #e8e8e8",
                  padding: "15px",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      paddingBottom: "5px",
                      borderBottom: "2px solid #E74C3C",
                    }}
                  >
                    <FaBuilding
                      style={{ color: "#E74C3C", fontSize: "0.9rem" }}
                    />
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#2c3e50",
                        fontSize: "0.95rem",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Empresa *
                    </span>
                  </div>
                  <Dropdown
                    value={empresaId}
                    options={empresasOptions}
                    onChange={(e) => setEmpresaId(e.value)}
                    placeholder="Selecione a empresa"
                    style={{
                      width: "100%",
                      height: "35px",
                      fontFamily: "'Rajdhani', sans-serif",
                      alignItems: "center",
                    }}
                    showClear
                    itemTemplate={(option) => (
                      <div
                        style={{
                          fontFamily: "'Rajdhani', sans-serif",
                          fontSize: "0.85rem",
                        }}
                      >
                        {option.label}
                      </div>
                    )}
                    valueTemplate={(option, props) => {
                      if (!option)
                        return (
                          <span
                            style={{
                              fontFamily: "'Rajdhani', sans-serif",
                              fontSize: "0.85rem",
                            }}
                          >
                            {props.placeholder}
                          </span>
                        );
                      return (
                        <span style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                          {option.label}
                        </span>
                      );
                    }}
                  />
                </div>
              </div>

              {/* Card Ativo */}
              <div
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #e8e8e8",
                  padding: "15px",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      paddingBottom: "5px",
                      borderBottom: "2px solid #2980B9",
                    }}
                  >
                    <FaCheckCircle
                      style={{ color: "#2980B9", fontSize: "0.9rem" }}
                    />
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#2c3e50",
                        fontSize: "0.95rem",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Ativo *
                    </span>
                  </div>
                  <Checkbox
                    checked={ativo}
                    onChange={(e) => setAtivo(e.checked)}
                    style={{
                      width: "100%",
                      height: "35px",
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Botão Fechar */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "15px",
              flexWrap: "wrap",
            }}
          >
            <Button
              label="CANCELAR"
              icon="pi pi-times"
              onClick={() => {
                resetarCampos();
                setVisibleEditarPedido(false);
              }}
              className="p-button-outlined p-button-sm"
              style={{
                outline: "none",
                boxShadow: "none",
                padding: "8px 24px",
                minWidth: "120px",
                borderColor: "#dee2e6",
                color: "#495057",
                fontWeight: "500",
                fontSize: "0.875rem",
              }}
              disabled={loading}
            />
            <Button
              label={loading ? "SALVANDO..." : "SALVAR ALTERAÇÕES"}
              icon="pi pi-check"
              onClick={() => handleEditar(pedidoEditandoId)}
              className="p-button-success p-button-sm"
              style={{
                outline: "none",
                boxShadow: "none",
                padding: "8px 24px",
                minWidth: "160px",
                fontWeight: "500",
                fontSize: "0.875rem",
              }}
              disabled={loading || !titulo || !descricao || !empresaId}
              loading={loading}
            />
          </div>
        </>
      </Dialog>

      {/* Dialog Itens do Pedido */}
      <Dialog
        visible={visibleItens}
        style={{
          width: "90vw",
          maxWidth: "768px",
          fontFamily: "'Rajdhani', sans-serif",
        }}
        onHide={() => !loading && setVisibleItens(false)}
        draggable={false}
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "-45px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
              }}
            >
              <div style={{ marginRight: "8px", paddingLeft: "8px" }}>
                <FaList style={{ fontSize: "2.4rem", color: "#B83732" }} />
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", flex: 1 }}
              >
                <div
                  style={{
                    fontSize: "0.9rem",
                    width: "100%",
                    letterSpacing: "1px",
                  }}
                >
                  ITENS DO
                </div>
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    marginTop: "-10px",
                  }}
                >
                  PEDIDO
                </div>
              </div>
            </div>
          </div>
        }
      >
        {/* Formulário em Cards */}
        <div style={{ padding: "20px 0" }}>
          {/* Card Principal - Controle de Itens */}
          <div
            style={{
              display: "grid",
              backgroundColor: "#fafafa",
              border: "1px solid #e8e8e8",
              padding: "15px",
              borderRadius: "6px",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "5px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                width: "100%",
              }}
            >
              {/* Cabeçalho do Card */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingBottom: "10px",
                  borderBottom: "2px solid #3498DB",
                }}
              >
                {/* Título do Card */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <FaList style={{ color: "#3498DB", fontSize: "1rem" }} />
                  <span
                    style={{
                      fontWeight: "600",
                      color: "#2c3e50",
                      fontSize: "1rem",
                      letterSpacing: "0.3px",
                    }}
                  >
                    Controle de Itens do Pedido
                  </span>
                </div>
              </div>

              {/* Botão Adicionar */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                  padding: "10px",
                  backgroundColor: "#fff",
                  border: "1px solid #e8e8e8",
                  borderRadius: "6px",
                }}
              >
                {/* Botão Adicionar */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                >
                  <Button
                    label="ADICIONAR"
                    icon="pi pi-plus"
                    onClick={handleAdicionarItem}
                    className="p-button-success p-button-sm"
                    style={{
                      outline: "none",
                      boxShadow: "0 2px 4px rgba(40, 167, 69, 0.2)",
                      padding: "6px 20px",
                      minWidth: "100px",
                      borderColor: "#28a745",
                      backgroundColor: "#28a745",
                      color: "white",
                      fontWeight: "600",
                      fontSize: "0.9rem",
                      fontFamily: "'Rajdhani', sans-serif",
                    }}
                  />
                </div>
              </div>

              {/* Área dos Itens */}
              <div style={{ marginBottom: "10px" }}>
                {(itensEditando || []).length > 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {itensEditando.map((item, index) => (
                      <div
                        key={item.id || `new-${index}`}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          padding: "12px",
                          backgroundColor: item.verificado
                            ? "#e8f5e9"
                            : "white",
                          borderRadius: "6px",
                          border: `1px solid ${item.verificado ? "#c3e6cb" : "#e8e8e8"}`,
                          transition: "all 0.2s ease",
                          position: "relative",
                        }}
                      >
                        {/* Linha superior: Controles e campos */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            width: "100%",
                          }}
                        >
                          {/* Número de Ordem */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "24px",
                              height: "24px",
                              backgroundColor: "#3498DB",
                              color: "white",
                              borderRadius: "50%",
                              fontSize: "0.8rem",
                              fontWeight: "600",
                              flexShrink: 0,
                            }}
                          >
                            {index + 1}
                          </div>

                          {/* Campo Descrição */}
                          <div style={{ flex: 1, position: "relative" }}>
                            <input
                              value={item.descricao || ""}
                              onChange={(e) =>
                                handleAtualizarItem(
                                  index,
                                  "descricao",
                                  e.target.value,
                                )
                              }
                              disabled={item.verificado}
                              placeholder="Descrição do item"
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                backgroundColor: item.verificado
                                  ? "#f8f9fa"
                                  : "#fff",
                                border: `1px solid ${item.verificado ? "#d6d6d6" : "#dee2e6"}`,
                                borderRadius: "4px",
                                outline: "none",
                                fontSize: "0.9rem",
                                fontWeight: "500",
                                color: item.verificado ? "#6c757d" : "#2c3e50",
                                fontFamily: "'Rajdhani', sans-serif",
                                cursor: item.verificado
                                  ? "not-allowed"
                                  : "text",
                              }}
                            />
                          </div>

                          {/* Campo Quantidade */}
                          <div style={{ position: "relative", flexShrink: 0 }}>
                            <input
                              type="number"
                              min="0"
                              value={item.quantidade}
                              onChange={(e) =>
                                handleAtualizarItem(
                                  index,
                                  "quantidade",
                                  parseInt(e.target.value) || 1,
                                )
                              }
                              disabled={item.verificado}
                              style={{
                                width: "50px",
                                padding: "8px",
                                backgroundColor: item.verificado
                                  ? "#f8f9fa"
                                  : "#fff",
                                border: `1px solid ${item.verificado ? "#d6d6d6" : "#dee2e6"}`,
                                borderRadius: "4px",
                                outline: "none",
                                fontSize: "0.9rem",
                                fontWeight: "500",
                                color: item.verificado ? "#6c757d" : "#2c3e50",
                                fontFamily: "'Rajdhani', sans-serif",
                                textAlign: "center",
                                cursor: item.verificado
                                  ? "not-allowed"
                                  : "text",
                              }}
                            />
                          </div>

                          {/* Botões de Ação */}
                          <div
                            style={{
                              display: "flex",
                              gap: "5px",
                              flexShrink: 0,
                            }}
                          >
                            {/* Botão Verificar */}
                            <Button
                              icon={
                                item.verificado
                                  ? "pi pi-check-circle"
                                  : "pi pi-circle"
                              }
                              className={`p-button-sm ${item.verificado ? "p-button-success" : "p-button-outlined"}`}
                              style={{
                                width: "34px",
                                height: "34px",
                                borderColor: item.verificado
                                  ? "#28a745"
                                  : "#28a745",
                                color: item.verificado ? "#fff" : "#28a745",
                                backgroundColor: item.verificado
                                  ? "#28a745"
                                  : "transparent",
                              }}
                              onClick={() => handleVerificarItem(index)}
                              tooltip={
                                item.verificado ? "Cancelar" : "Confirmar"
                              }
                              disabled={!item.descricao?.trim()}
                            />

                            {/* Botão Remover */}
                            {!item.verificado && (
                              <Button
                                icon="pi pi-trash"
                                onClick={() => handleRemoverItem(index)}
                                className="p-button-danger p-button-sm"
                                style={{
                                  width: "34px",
                                  height: "34px",
                                  flexShrink: 0,
                                }}
                                tooltip="Remover"
                                tooltipOptions={{ position: "top" }}
                                disabled={loading}
                              />
                            )}
                          </div>
                        </div>

                        {/* Ícone de item verificado */}
                        {item.verificado && (
                          <div
                            style={{
                              position: "absolute",
                              top: "-8px",
                              right: "-8px",
                              backgroundColor: "#28a745",
                              color: "white",
                              borderRadius: "50%",
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.6rem",
                              fontWeight: "bold",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            }}
                          >
                            ✓
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "10px 5px",
                      backgroundColor: "#fafafa",
                      border: "2px dashed #dee2e6",
                      borderRadius: "6px",
                      textAlign: "center",
                    }}
                  >
                    <FaClipboardList
                      style={{
                        fontSize: "2.5rem",
                        color: "#adb5bd",
                        opacity: 0.5,
                      }}
                    />
                    <h4
                      style={{
                        fontSize: "1rem",
                        color: "#6c757d",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      Nenhum item cadastrado
                    </h4>
                    <p
                      style={{
                        fontSize: "0.85rem",
                        color: "#adb5bd",
                        margin: 0,
                        lineHeight: "1.4",
                      }}
                    >
                      Clique no botão "ADICIONAR ITEM" para incluir itens no
                      pedido.
                    </p>
                  </div>
                )}
              </div>

              {/* Resumo e Estatísticas */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  paddingTop: "5px",
                  borderTop: "1px solid #e8e8e8",
                }}
              >
                {/* Estatísticas */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      fontSize: "0.85rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FaClipboardList
                        style={{ fontSize: "0.9rem", color: "#7f8c8d" }}
                      />
                      <span style={{ color: "#6c757d" }}>
                        <strong>{(itensEditando || []).length}</strong> item(s)
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      fontSize: "0.85rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FaCheckCircle
                        style={{ fontSize: "0.9rem", color: "#28a745" }}
                      />
                      <span style={{ color: "#28a745" }}>
                        <strong>
                          {
                            (itensEditando || []).filter(
                              (item) => item.verificado,
                            ).length
                          }
                        </strong>{" "}
                        verificado(s)
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FaClock
                        style={{ fontSize: "0.9rem", color: "#6c757d" }}
                      />
                      <span style={{ color: "#6c757d" }}>
                        <strong>
                          {
                            (itensEditando || []).filter(
                              (item) => !item.verificado,
                            ).length
                          }
                        </strong>{" "}
                        pendente(s)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Botões de Ação */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "15px",
            flexWrap: "wrap",
          }}
        >
          <Button
            label="CANCELAR"
            icon="pi pi-times"
            onClick={() => {
              setVisibleItens(false);
            }}
            className="p-button-outlined p-button-sm"
            style={{
              outline: "none",
              boxShadow: "none",
              padding: "6px 20px",
              minWidth: "100px",
              borderColor: "#dee2e6",
              color: "#495057",
              fontWeight: "500",
              fontSize: "0.875rem",
            }}
            disabled={loading}
          />
          <Button
            label={loading ? "SALVANDO..." : "SALVAR ALTERAÇÕES"}
            icon="pi pi-check"
            onClick={handleSalvarItens}
            className="p-button-success p-button-sm"
            style={{
              outline: "none",
              boxShadow: "none",
              padding: "8px 24px",
              minWidth: "150px",
              fontWeight: "500",
              fontSize: "0.875rem",
            }}
            disabled={loading}
            loading={loading}
          />
        </div>
      </Dialog>

      {/* Barra superior vermelha */}
      <div
        style={{
          backgroundColor: "#B83732",
          height: "40px",
          minHeight: "40px",
          width: "100%",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          fontWeight: "bold",
          position: "fixed",
          zIndex: 1, 
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        {/* lado esquerdo */}
        <div></div>

        {/* Centro */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button
            icon="pi pi-refresh"
            className="icon-button-titulo"
            onClick={() => {
              fetchPedidos();
            }}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
          <Button
            icon={mostrarFiltroPedidos ? "pi pi-filter-slash" : "pi pi-filter"}
            className="icon-button-titulo"
            onClick={() => {
              setMostrarFiltroPedidos(!mostrarFiltroPedidos);
            }}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
          <Button
            icon="pi pi-plus"
            className="icon-button-titulo"
            onClick={() => {
              if (
                user.tipo === "administrator" ||
                user.modulos?.Pedidos?.editar
              ) {
                setVisibleNovoPedido(true);
                resetarCampos();
              }
            }}
            disabled={
              !(user.tipo === "administrator" || user.modulos?.Pedidos?.editar)
            }
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
              opacity:
                user.tipo === "administrator" || user.modulos?.Pedidos?.editar
                  ? 1
                  : 0.4,
              cursor:
                user.tipo === "administrator" || user.modulos?.Pedidos?.editar
                  ? "pointer"
                  : "not-allowed",
            }}
            outlined
          />
        </div>

        {/* lado direito */}
        <div></div>
      </div>

       {/* Conteúdo abaixo da barra */}
      <div style={{ flex: 1, padding: "50px 10px" }}>
        {/* MOSTRAR RESERVAS DO DIA SELECIONADO - SEMPRE VISÍVEL */}
        <div style={{ borderRadius: "8px", padding: "0 15px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <div style={{ marginTop: "7px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "8px",
                  gap: "5px",
                }}
              >
                <FaClipboardList
                  style={{ color: "#B83732", fontSize: "1.2rem" }}
                />
                <h3 style={{ margin: 0, color: "#424242" }}>PEDIDOS</h3>
              </div>
            </div>
          </div>

          {/* InputSwitch */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              marginBottom: "30px",
            }}
          ></div>

          {/* Filtros para Empresa */}
          {mostrarFiltroPedidos && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  borderRadius: "8px",
                  padding: "10px 8px",
                  border: "1px solid #dee2e6",
                  backgroundColor: "#fff",
                  width: "95%",
                  maxWidth: "520px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                {/* 1ª Linha - Nome e CPF */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(200px, 1fr))",
                    gap: "10px",
                  }}
                >
                  {/* Card Nome */}
                  <div style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          paddingBottom: "5px",
                          borderBottom: "2px solid #3498DB",
                        }}
                      >
                        <FaClipboardList
                          style={{ color: "#3498DB", fontSize: "0.9rem" }}
                        />
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#2c3e50",
                            fontSize: "0.95rem",
                            letterSpacing: "0.3px",
                          }}
                        >
                          Título do Pedido{" "}
                        </span>
                      </div>
                      <InputText
                        className="p-inputtext-sm"
                        placeholder="Título do pedido"
                        value={filtroTitulo}
                        onChange={(e) => setFiltroTitulo(e.target.value)}
                        style={{
                          outline: "none",
                          boxShadow: "none",
                          width: "100%",
                          height: "35px",
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      />
                    </div>
                  </div>

                  {/* Card CNPJ */}
                  <div style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          paddingBottom: "5px",
                          borderBottom: "2px solid #E67E22",
                        }}
                      >
                        <FaClipboardList
                          style={{ color: "#E67E22", fontSize: "0.9rem" }}
                        />
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#2c3e50",
                            fontSize: "0.95rem",
                            letterSpacing: "0.3px",
                          }}
                        >
                          Descrição do Pedido{" "}
                        </span>
                      </div>
                      <InputText
                        className="p-inputtext-sm"
                        placeholder="Descrição do pedido"
                        value={filtroDescricao}
                        onChange={(e) => setFiltroDescricao(e.target.value)}
                        style={{
                          outline: "none",
                          boxShadow: "none",
                          width: "100%",
                          height: "35px",
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Grids */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              padding: "10px 8px",
            }}
          >
            {pedidosFiltrados.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  border: "2px dashed #e8e8e8",
                }}
              >
                <FaClipboardList
                  style={{
                    fontSize: "3rem",
                    color: "#B83732",
                    marginBottom: "15px",
                    opacity: 0.5,
                  }}
                />
                <h3
                  style={{
                    color: "#2c3e50",
                    margin: "0 0 10px 0",
                    fontSize: "1.2rem",
                    fontWeight: "600",
                  }}
                >
                  Nenhum pedido encontrado
                </h3>
              </div>
            ) : (
              <div
                className="grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", // Alterado para 280px
                  gap: "15px",
                  alignItems: "stretch",
                }}
              >
                {pedidosFiltrados.map((item, idx) => (
                  <div key={idx}>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e8e8e8",
                        borderRadius: "10px",
                        padding: "12px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {/* Informações do Paciente */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          marginBottom: "5px",
                          paddingBottom: "5px",
                          borderBottom: "1px solid #e8e8e8",
                        }}
                      >
                        <FaClipboardList
                          style={{
                            fontSize: "2.5rem",
                            color: "#D65A55",
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <h3
                            style={{
                              fontSize: "1rem",
                              fontWeight: "600",
                              color: "#2c3e50",
                              margin: "0",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.titulo}
                          </h3>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              color: "#64748b",
                              fontSize: "0.85rem",
                            }}
                          >
                            {item.descricao}
                          </div>
                        </div>
                      </div>

                      {/* Detalhes do Agendamento */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr",
                          gap: "6px",
                          marginBottom: "5px",
                        }}
                      >
                        {/* Coluna 1 */}
                        <div
                          style={{
                            display: "flex",
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e8e8e8",
                            padding: "6px",
                            borderRadius: "6px",
                            flexDirection: "column",
                            gap: "5px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            <FaBuilding
                              style={{ color: "#2980b9", fontSize: "0.75rem" }}
                            />{" "}
                            {/* Reduzido */}
                            <span
                              style={{
                                fontSize: "0.8rem", // Reduzido
                                color: "#2c3e50",
                                fontWeight: "500",
                              }}
                            >
                              {item.empresa_nome}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Ações Secundárias */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          paddingTop: "5px",
                          gap: "5px",
                          marginTop: "auto",
                          borderTop: "1px solid #e8e8e8",
                        }}
                      >
                        {/* Ativo */}
                        <div
                          style={{
                            display: "flex",
                            gap: "5px",
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              fontFamily: "'Rajdhani', sans-serif",
                              gap: "6px",
                              padding: "2px 6px",
                              borderRadius: "12px",
                              fontSize: "0.75rem",
                              fontWeight: "500",
                              color: item.ativo === "1" ? "#27ae60" : "#e74c3c",
                            }}
                          >
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                backgroundColor:
                                  item.ativo === "1" ? "#27ae60" : "#e74c3c",
                              }}
                            />
                          </div>
                        </div>

                        {/* Botoes */}
                        <div
                          style={{
                            display: "flex",
                            gap: "5px",
                          }}
                        >
                          <Button
                            icon="pi pi-file-edit"
                            onClick={() => {
                              if (
                                user.tipo === "administrator" ||
                                user.modulos?.Pedidos?.editar
                              ) {
                                setPedidoEditandoId(item.id);

                                // Preenche os campos do formulário
                                setTitulo(item.titulo || "");
                                setDescricao(item.descricao || "");
                                setEmpresaId(item.empresa_id || "");
                                setAtivo(item.ativo === "1");
                                setVisibleEditarPedido(true);
                              }
                            }}
                            disabled={
                              !(
                                user.tipo === "administrator" ||
                                user.modulos?.Pedidos?.editar
                              )
                            }
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                              opacity:
                                user.tipo === "administrator" ||
                                user.modulos?.Empresas?.editar
                                  ? 1
                                  : 0.4,
                              cursor:
                                user.tipo === "administrator" ||
                                user.modulos?.Empresas?.editar
                                  ? "pointer"
                                  : "not-allowed",
                            }}
                            tooltip="Editar"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-list"
                            onClick={async () => {
                              if (
                                user.tipo === "administrator" ||
                                user.modulos?.Pedidos?.editar
                              ) {
                                setPedidoItensEditandoId(item.id);
                                await buscarItensPedido(item.id);
                                setVisibleItens(true);
                              }
                            }}
                            disabled={
                              !(
                                user.tipo === "administrator" ||
                                user.modulos?.Pedidos?.editar
                              )
                            }
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                              opacity:
                                user.tipo === "administrator" ||
                                user.modulos?.Empresas?.editar
                                  ? 1
                                  : 0.4,
                              cursor:
                                user.tipo === "administrator" ||
                                user.modulos?.Empresas?.editar
                                  ? "pointer"
                                  : "not-allowed",
                            }}
                            tooltip="Itens"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-trash"
                            onClick={() => {
                              setShowConfirmDialog(true);
                              openDeleteConfirm(item.id);
                            }}
                            disabled={
                              !(
                                user.tipo === "administrator" ||
                                user.modulos?.Pedidos?.editar
                              )
                            }
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                              opacity:
                                user.tipo === "administrator" ||
                                user.modulos?.Empresas?.editar
                                  ? 1
                                  : 0.4,
                              cursor:
                                user.tipo === "administrator" ||
                                user.modulos?.Empresas?.editar
                                  ? "pointer"
                                  : "not-allowed",
                            }}
                            tooltip="Excluir"
                            tooltipOptions={{ position: "top" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading Spinner */}
      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <ProgressSpinner
            style={{ width: "50px", height: "50px" }}
            strokeWidth="8"
          />
        </div>
      )}
    </div>
  );
};

export default Pedidos;
